import { registerSW } from "virtual:pwa-register";

/**
 * Service worker registration and the update loop.
 *
 * The pitfall this exists to avoid: an installed PWA that pins itself to an old
 * build. Browsers only check for a new service worker on navigation, and an
 * installed app on a phone can go weeks without one — so we check on a timer
 * and whenever the app comes back to the foreground.
 *
 * When a new version is waiting the app shows a banner rather than reloading
 * underneath someone mid-edit. Every edit saves immediately (the app has no
 * save buttons), so accepting the reload can never lose work.
 */

/** How often to ask the browser whether a new build exists. */
const UPDATE_INTERVAL_MS = 60 * 60 * 1000;

export type UpdateListener = (updateAvailable: boolean) => void;

let applyUpdate: (() => Promise<void>) | null = null;

export function initServiceWorker(onUpdate: UpdateListener): void {
  if (import.meta.env.DEV) return;

  const update = registerSW({
    immediate: true,
    onNeedRefresh() {
      onUpdate(true);
    },
    onRegisteredSW(_url, registration) {
      if (!registration) return;

      const check = () => {
        // Only worth asking when we are actually online.
        if (navigator.onLine) void registration.update();
      };

      setInterval(check, UPDATE_INTERVAL_MS);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") check();
      });
      window.addEventListener("online", check);
    },
  });

  applyUpdate = async () => {
    await update(true);
  };
}

/** Activate the waiting service worker and reload into the new build. */
export async function reloadForUpdate(): Promise<void> {
  if (applyUpdate) {
    await applyUpdate();
  } else {
    window.location.reload();
  }
}
